import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Box, Checkbox, Flex, Icon, Input, Button, VStack, Text } from '@chakra-ui/react'
import { XCloseIcon, CheckIcon, FileQuestion02Icon } from '../../assets/icons'
import FeedbackModal from '../modals/FeedbackModal'

interface CheckboxCardProps {
  answers?: any[]
  onAnswersChange: (answers: any[]) => void
  training?: boolean
  forceSelectingCorrect?: boolean
  onForceCancel?: () => void
  onForceDone?: () => void
  correct_feedback?: string
  incorrect_feedback?: string
  feedback?: { correct_feedback: string; incorrect_feedback: string }
  onFeedbackChange?: (feedback: { correct_feedback: string; incorrect_feedback: string }) => void
  quizMode?: boolean
  quizSubmitted?: boolean
  questionId?: string
  onCheckboxToggle?: (questionId: string, optionIndex: number, isCorrect: boolean) => void
  handleFeedbackDisplay?: (result: boolean) => void
  readOnly?: boolean
  userAnswer?: string[]
  isIndividualResponse?: boolean
  // Hydrated user selections from parent (Questionnaire/MasterCard)
  checkboxSelectedIndexes?: number[] | null
}

const CheckboxCard = forwardRef<
  {
    doneSelectCorrect: () => void
    cancelSelectCorrect: () => void
  },
  CheckboxCardProps
>(
  (
    {
      answers = [],
      onAnswersChange,
      training = false,
      forceSelectingCorrect,
      quizMode = false,
      quizSubmitted = false,
      feedback = { correct_feedback: '', incorrect_feedback: '' },
      onFeedbackChange,
      onCheckboxToggle,
      questionId,
      handleFeedbackDisplay,
      userAnswer = [],
      isIndividualResponse = false,
      checkboxSelectedIndexes = null,
    },
    ref,
  ) => {
    const initOptions = () => {
      let opts
      if (training) {
        opts =
          Array.isArray(answers) && answers.length > 0 && typeof answers[0] === 'object'
            ? answers.slice()
            : [{ text: 'Option 1', correct: false }]
      } else {
        opts = Array.isArray(answers) && answers.length > 0 ? answers.slice() : ['Option 1']
      }
      if (
        opts.length === 0 ||
        (training ? opts[opts.length - 1].text.trim() !== '' : opts[opts.length - 1].trim() !== '')
      ) {
        opts.push(training ? { text: '', correct: false } : '')
      }
      return opts
    }

    const [options, setOptions] = useState<any[]>(initOptions)
    const inputRefs = useRef<(HTMLInputElement | null)[]>([])
    const [error, setError] = useState<string>('')
    
    const [selectingCorrect, setSelectingCorrect] = useState<boolean>(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [localFeedback, setLocalFeedback] = useState(feedback)
    const [quizSelections, setQuizSelections] = useState<number[]>([])

    useEffect(() => {
      onAnswersChange(
        options.filter((opt, idx) =>
          training
            ? idx !== options.length - 1 || opt.text.trim() !== ''
            : idx !== options.length - 1 || opt.trim() !== '',
        ),
      )
    }, [options, training])

    useEffect(() => {
      if (!quizMode) {
        setQuizSelections([])
      }
    }, [quizMode])

    // Hydrate local selection state from parent-provided selections
    useEffect(() => {
      if (quizMode) {
        if (Array.isArray(checkboxSelectedIndexes)) {
          setQuizSelections(checkboxSelectedIndexes)
        }
      }
    }, [checkboxSelectedIndexes, quizMode])

    const validateOption = (index: number, value: string): boolean => {
      if (value.trim() === '') {
        setError('Option cannot be empty.')
        return false
      }
      const otherOptions = options.filter((_, i) => i !== index)
      if (otherOptions.some((opt) => (training ? opt.text === value.trim() : opt === value.trim()))) {
        setError('Duplicate options are not allowed.')
        return false
      }
      return true
    }

    const handleOptionChange = (index: number, value: string) => {
      setError('')
      let newOpts = options.map((opt, i) => {
        if (i === index) {
          return training ? { ...opt, text: value } : value
        }
        return opt
      })
      if (index === options.length - 1) {
        if (training ? value.trim() !== '' : value.trim() !== '') {
          newOpts = [...newOpts, training ? { text: '', correct: false } : '']
        }
      }
      setOptions(newOpts)
    }

    const handleOptionBlur = (index: number, value: string) => {
      if (!validateOption(index, value)) {
        setTimeout(() => {
          inputRefs.current[index]?.focus()
        }, 0)
        return
      }
      handleOptionChange(index, value.trim())
    }

    const removeOption = (index: number) => {
      let updated = options.filter((_, i) => i !== index)
      if (
        updated.length === 0 ||
        (training ? updated[updated.length - 1].text.trim() !== '' : updated[updated.length - 1].trim() !== '')
      ) {
        updated.push(training ? { text: '', correct: false } : '')
      }
      setOptions(updated)
    }

    const toggleCorrect = (index: number) => {
      if (!training) return
      const updated = options.map((opt, i) => (i === index ? { ...opt, correct: !opt.correct } : opt))
      setOptions(updated)
    }

    const doneSelectCorrect = () => {
      setSelectingCorrect(false)
    }

    const cancelSelectCorrect = () => {
      setSelectingCorrect(false)
    }

    useImperativeHandle(ref, () => ({
      doneSelectCorrect,
      cancelSelectCorrect,
    }))

    const isSelecting = typeof forceSelectingCorrect === 'boolean' ? forceSelectingCorrect : selectingCorrect

    const handleOpenModal = () => {
      setIsModalOpen(true)
    }

    const handleFeedbackChange = (newFeedback: { correct_feedback: string; incorrect_feedback: string }) => {
      setLocalFeedback(newFeedback)
      onFeedbackChange?.(newFeedback)
    }

    const handleIsQuestionCorrect = (result: boolean) => {
      handleFeedbackDisplay?.(result)
    }

    const handleCheckboxToggle = (index: number) => {
      if (quizMode && !quizSubmitted) {

        const already = quizSelections.includes(index)
        const updatedSelections = already ? quizSelections.filter((i) => i !== index) : [...quizSelections, index]

        setQuizSelections(updatedSelections)
        const isCorrect = training ? !!options[index]?.correct : false
        // Pass the index and let the parent component access the text
        onCheckboxToggle && onCheckboxToggle(questionId ?? '', index, isCorrect)
      } else if (!quizMode && training) {
        toggleCorrect(index)
      }
    }

    return (
      <>
        <Box className="checkbox-card" paddingLeft="20px">
          <VStack direction="column" w="100%" align="flex-start" spacing="8px">
            {options.map((option, index) => {
              const isLastOption = index === options.length - 1
              if ((isSelecting || quizMode) && isLastOption) return null

              const userSelected = Array.isArray(userAnswer) && userAnswer.includes(option.text)
              let isChecked = false
              let variantToUse = 'success'

              if (isIndividualResponse) {
                if (option.correct) {
                  isChecked = true
                  variantToUse = 'success'
                }
                if (userSelected && !option.correct) {
                  isChecked = true
                  variantToUse = 'error'
                }
              } else if (quizMode && quizSubmitted) {
                if (quizSelections.includes(index)) {
                  isChecked = true
                  variantToUse = option.correct ? 'success' : 'error'
                } else if (option.correct) {
                  isChecked = true
                  variantToUse = 'success'
                } else {
                  isChecked = false
                }
              } else if (quizMode) {
                isChecked = quizSelections.includes(index)
                variantToUse = 'success'
              } else if (training) {
                isChecked = !!option.correct
                variantToUse = 'success'
              }

              if (quizMode && quizSubmitted && index === options.length - 2) {
                const allCorrect = options.every((opt, idx) => opt.correct === quizSelections.includes(idx))
                handleIsQuestionCorrect(allCorrect)
              }

              return (
                <Flex
                  key={index}
                  w="100%"
                  align="center"
                  px="10px"
                  h="40px"
                  borderRadius="10px"
                  bg={!quizMode && training && isSelecting && option.correct ? 'success.25' : 'transparent'}
                >
                  <Checkbox
                    size="lg"
                    mr="6px"
                    variant={variantToUse}
                    isChecked={isChecked}
                    pointerEvents={isIndividualResponse ? 'none' : 'auto'}
                    isDisabled={training && !isSelecting}
                    onChange={() => handleCheckboxToggle(index)}
                  />
                  <Flex align="center" w="100%" justifyContent="space-between">
                    <Input
                      whiteSpace="pre-wrap"
                      ref={(el) => (inputRefs.current[index] = el)}
                      value={training ? option.text : option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      onBlur={(e) => handleOptionBlur(index, e.target.value)}
                      variant="multipleChoice"
                      onFocus={(e) => e.target.select()}
                      isReadOnly={quizMode}
                      bg="transparent"
                      color="White.Muted[600]"
                      size="xs"
                      placeholder={index === options.length - 1 ? 'Add option' : ''}
                    />
                    {!quizMode && !isSelecting && (
                      <Button variant="icon" onClick={() => removeOption(index)}>
                        <Icon boxSize="24px" color="solid.500">
                          <XCloseIcon />
                        </Icon>
                      </Button>
                    )}
                    {training && isSelecting && option.correct && !quizMode && (
                      <Icon boxSize="16px" color="success.100">
                        <CheckIcon />
                      </Icon>
                    )}
                  </Flex>
                </Flex>
              )
            })}
            {!quizMode && training && !isSelecting && (
              <Box
                as="button"
                px="8px"
                h="40px"
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                color="primary.100"
                onClick={handleOpenModal}
                sx={{
                  _focus: { boxShadow: 'none', outline: 'none' },
                }}
                _hover={{ borderColor: 'transparent' }}
              >
                <Icon boxSize="24px" mr="15px">
                  <FileQuestion02Icon />
                </Icon>
                <Text variant="h-xs">Add Answer Feedback</Text>
              </Box>
            )}
            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </VStack>
        </Box>

        {isModalOpen && (
          <FeedbackModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            feedback={localFeedback}
            onFeedbackChange={handleFeedbackChange}
          />
        )}
      </>
    )
  },
)

export default CheckboxCard
